/* USAGE 

Example: <Button text="LOGIN" icon={faArrowRight} type="primary" />

Props:
  "text" - the text on the button - REQUIRED
  "icon" - the FontAwesome icon - OPTIONAL (will automatically add/remove class if icon is present)
  "type" - The type of the button - REQUIRED at least one type: "primary" or "secondary" or "primary-small" or "secondary-small" or "tertiary" or "multi-line" - defaults to "primary"
  "topText" - Objects with "text" and "color" properties IF the button is of type "multi-line" - OPTIONAL 
  "loading" - Boolean if the button is loading - OPTIONAL
  "disabled" - Boolean if the button is disabled - OPTIONAL
  "children" - Other content inside the button as children - OPTIONAL
  "className" - Allow additional classes to be passed to the component
  "onClick" - Function to be called when the button is clicked - OPTIONAL
  
*/

import { IconName, IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";

interface Props {
  text: string;
  icon?: IconName | IconProp;
  type?:
    | "primary"
    | "primary-blue-btn"
    | "secondary"
    | "secondary-blue-btn"
    | "primary-small"
    | "primary-small-blue-btn"
    | "secondary-small"
    | "secondary-small-blue-btn"
    | "tertiary"
    | "tertiary-small"
    | "decline"
    | "primary-red-btn"
    | "primary-small-red-btn"
    | "decline-small"
    | "btn-decline-small"
    | "multi-line";
  topText?: {
    text: string;
    color?: "green" | "gray";
  };
  color?: string;
  textColor?: string;
  buttonType?: "button" | "submit" | "reset";
  children?: React.ReactNode;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  spinIcon?: boolean;
  onClick?: () => void;
  shouldStopPropagation?: boolean;
}

export const WButton = ({ type = "primary", ...props }: Props) => {
  return (
    <button
      type={props.buttonType || "button"}
      onClick={(event) => {
        if (props?.shouldStopPropagation) {
          event?.stopPropagation();
        }
        props?.onClick && props?.onClick();
      }}
      disabled={props.loading || props.disabled}
      style={{
        backgroundColor: props.color,
      }}
      className={`${
        type === "primary"
          ? "primary-btn"
          : type === "primary-blue-btn"
            ? "primary-blue-btn"
            : type === "secondary"
              ? "secondary-btn"
              : type === "secondary-blue-btn"
                ? "secondary-blue-btn"
                : type === "primary-small"
                  ? "primary-small-btn"
                  : type === "primary-small-blue-btn"
                    ? "primary-small-blue-btn"
                    : type === "secondary-small"
                      ? "secondary-small-btn"
                      : type === "secondary-small-blue-btn"
                        ? "secondary-small-blue-btn"
                        : type === "decline"
                          ? "decline-btn"
                          : type === "decline-small"
                            ? "decline-btn-small"
                            : type === "tertiary"
                              ? "tertiary-btn"
                              : type === "tertiary-small"
                                ? "tertiary-small-btn"
                                : type === "multi-line"
                                  ? "multi-line-btn"
                                  : type === "primary-red-btn"
                                    ? "primary-red-btn"
                                    : type === "primary-small-red-btn"
                                      ? "primary-small-red-btn"
                                      : type === "btn-decline-small"
                                        ? "btn-decline-small"
                                        : ""
      } ${props.icon ? "with-icon" : ""} ${
        props.className ? props.className : ""
      } ${
        props.loading || props.disabled ? "pointer-events-none opacity-50" : ""
      }
       ${props.color ? "hover:bg-opacity-80" : ""}`}
    >
      {type === "multi-line" && (
        <div
          className={clsx([
            "text-xs",
            props.topText?.color === "green" ? "text-green" : "text-gray",
          ])}
        >
          {props.topText?.text}
        </div>
      )}
      {props.children}
      <span style={{ color: props.textColor }}>{props.text}</span>
      {props.icon && (
        <FontAwesomeIcon
          icon={props.icon as IconName}
          size="lg"
          className={clsx([props.spinIcon && "fa-spin"])}
        />
      )}
      {props.loading && (
        <FontAwesomeIcon
          icon={"spinner-third"}
          className="absolute right-10 fa-spin"
        />
      )}
    </button>
  );
};

export default WButton;
